import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import ResponsiveImage from "../components/ResponsiveImage";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./ProductDetails.css";
import { useCart } from "../context/CartContext";

// Change this value when testing image format changes to avoid old browser cache.
const IMAGE_PROXY_VERSION = "optimized-v3";

const normalizeUploadPath = (value) =>
  String(value || "").replace(
    /\/uploads\/public\/products\/mayachitro\/photo-downloads\//gi,
    "/uploads/public/products/"
  );

const DEFAULT_RECEIVE_ITEMS = [
  "High-resolution JPG",
  "No watermark in paid file",
  "Personal use license",
  "Commercial license option",
  "License PDF included",
  "Usage guide included",
];

const getPublicUploadPath = (value = "") => {
  if (!value || typeof value !== "string") return "";

  const rawPath = normalizeUploadPath(value.trim());

  if (!rawPath) return "";

  if (/^(https?:)?\/\//i.test(rawPath)) {
    try {
      const url = new URL(rawPath, window.location.origin);
      return url.pathname.startsWith("/uploads/public/") ? url.pathname : "";
    } catch {
      return "";
    }
  }

  return rawPath.startsWith("/uploads/public/") ? rawPath : "";
};

const toSmartImageUrl = (publicUploadPath) => {
  const separator = publicUploadPath.includes("?") ? "&" : "?";
  return buildPublicFileUrl(
    `${publicUploadPath}${separator}v=${IMAGE_PROXY_VERSION}`
  );
};

const resolveAssetUrl = (path) => {
  if (!path || typeof path !== "string") return "/images/logo192.png";

  const rawPath = normalizeUploadPath(path.trim());

  if (!rawPath) return "/images/logo192.png";

  if (rawPath.startsWith("data:")) {
    return rawPath;
  }

  if (rawPath.includes("/api/image?")) {
    return rawPath;
  }

  const publicUploadPath = getPublicUploadPath(rawPath);

  if (publicUploadPath) {
    return toSmartImageUrl(publicUploadPath);
  }

  if (rawPath.startsWith("/uploads")) {
    return buildPublicFileUrl(rawPath);
  }

  if (rawPath.startsWith("/")) {
    return rawPath;
  }

  return rawPath;
};

const resolveVideoUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const rawPath = normalizeUploadPath(path.trim());
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  if (rawPath.startsWith("/uploads/public/")) return buildPublicFileUrl(rawPath);
  if (rawPath.startsWith("/")) return rawPath;
  return rawPath;
};

const isVideoMedia = (item) => {
  const type = String(item?.mediaType || "").toLowerCase();
  const path = String(item?.filePath || "");
  return type === "video" || /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
};

const getFileName = (path = "") => {
  const cleanPath = String(path || "").split("?")[0].split("#")[0];
  return cleanPath.substring(cleanPath.lastIndexOf("/") + 1);
};

const getFolderPath = (path = "") => {
  const cleanPath = String(path || "").split("?")[0].split("#")[0];
  const lastSlash = cleanPath.lastIndexOf("/");

  if (lastSlash === -1) return "";

  return cleanPath.substring(0, lastSlash);
};

const stripImageExtension = (value = "") => {
  return String(value || "")
    .trim()
    .replace(/\.(avif|webp|jpg|jpeg|png)$/i, "");
};

const stripImageVariant = (value = "") => {
  return stripImageExtension(value)
    .replace(/-card$/i, "")
    .replace(/-thumb$/i, "");
};

const getImageBaseFromPath = (path = "") => {
  const fileName = getFileName(path);

  if (!fileName) return "main";

  return stripImageVariant(fileName) || "main";
};

const parseArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const boolFromAny = (value) => {
  return value === true || value === 1 || value === "1";
};

const normalizeMediaItem = (item, fallbackAlt = "Product image") => {
  if (!item) return null;

  if (typeof item === "string") {
    return {
      mediaType: "gallery",
      filePath: item,
      fileName: getFileName(item),
      imageBase: getImageBaseFromPath(item),
      folderName: getFolderPath(item),
      altText: fallbackAlt,
      sortOrder: 999,
      isPrimary: false,
    };
  }

  const filePath =
    item.filePath ||
    item.file_path ||
    item.path ||
    item.url ||
    item.image ||
    item.src ||
    item.jpg_path ||
    item.webp_path ||
    item.avif_path ||
    item.original_path ||
    "";

  if (!filePath) return null;

  const folderName =
    item.folderName ||
    item.folder_name ||
    item.publicBasePath ||
    item.public_base_path ||
    getFolderPath(filePath);

  const imageBase =
    item.imageBase ||
    item.image_base ||
    item.mainImageBase ||
    item.main_image_base ||
    getImageBaseFromPath(filePath);

  return {
    mediaType: item.mediaType || item.media_type || item.type || "gallery",
    filePath,
    fileName: item.fileName || item.file_name || getFileName(filePath),
    imageBase,
    folderName,
    altText: item.altText || item.alt_text || fallbackAlt,
    sortOrder: Number(item.sortOrder ?? item.sort_order ?? 999),
    isPrimary: boolFromAny(item.isPrimary ?? item.is_primary),
  };
};

const cleanReceiveItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return DEFAULT_RECEIVE_ITEMS;

  const cleaned = items
    .filter(Boolean)
    .map((item) => String(item).trim())
    .filter(Boolean)
    .map((item) => {
      const lower = item.toLowerCase();

      if (lower === "instant download") return "Instant access";
      if (lower === "botanical wall art suitable file") {
        return "Printable wall art ready";
      }
      if (lower === "jpg file") return "High-resolution JPG";
      if (lower === "pdf file") return "License PDF included";

      return item;
    });

  return cleaned.length ? cleaned : DEFAULT_RECEIVE_ITEMS;
};

const getProductBrandSlug = (product) => {
  return String(product?.brandSlug || product?.brand_slug || product?.brand || "")
    .trim()
    .toLowerCase();
};

const getProductKey = (product) => {
  return String(product?.productId || product?.product_id || product?.id || product?._id || "");
};

const getProductFolderName = (product) => {
  return (
    product?.folderName ||
    product?.folder_name ||
    product?.imageFolder ||
    product?.image_folder ||
    product?.productFolder ||
    product?.product_folder ||
    product?.folder ||
    ""
  );
};

const getProductMainImageBase = (product) => {
  return (
    product?.mainImageBase ||
    product?.main_image_base ||
    product?.imageBase ||
    product?.image_base ||
    getImageBaseFromPath(
      product?.image ||
        product?.mainImage ||
        product?.main_image ||
        product?.coverImage ||
        product?.cover_image ||
        product?.previewImage ||
        product?.preview_image ||
        ""
    ) ||
    "main"
  );
};

const hasOptimizedSource = (product, mediaItem) => {
  return Boolean(
    mediaItem?.folderName ||
      getProductFolderName(product) ||
      product?.publicBasePath ||
      product?.public_base_path ||
      mediaItem?.filePath?.startsWith("/uploads/public/")
  );
};

const ProductDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});
  const [customerEmail, setCustomerEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchProduct = async () => {
    setLoading(true);
    setError("");

    try {
      const [productResponse, productsResponse] = await Promise.all([
        fetch(buildApiUrl(`/products/${slug}`), {
          headers: { Accept: "application/json" },
        }),
        fetch(buildApiUrl("/products?status=published&brand=mayachitro"), {
          headers: { Accept: "application/json" },
        }),
      ]);

      const productData = await productResponse.json();
      const productsData = await productsResponse.json();

      if (!productResponse.ok || !productData.success) {
        throw new Error(productData.message || "Product not found.");
      }

      setProduct(productData.product);
      setAllProducts(productsData.products || []);
      setSelectedLicense(0);
      setSelectedImageIndex(0);
      setBrokenImages({});
      setCheckoutError("");
      setCheckoutLoading(false);
    } catch (err) {
      setError(err.message || "Could not load product.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const productGallery = useMemo(() => {
    if (!product) return [];

    const fallbackAlt =
      product.altText || product.alt_text || product.title || "Product image";

    const directMedia = [
      ...parseArray(product.productMedia),
      ...parseArray(product.product_media),
      ...parseArray(product.media),
      ...parseArray(product.mediaItems),
      ...parseArray(product.media_items),
      ...parseArray(product.gallery),
      ...parseArray(product.previewImages),
      ...parseArray(product.preview_images),
      ...parseArray(product.previewImagesJson),
      ...parseArray(product.preview_images_json),
      ...parseArray(product.mockups),
      ...parseArray(product.mockupsJson),
      ...parseArray(product.mockups_json),
    ]
      .map((item) => normalizeMediaItem(item, fallbackAlt))
      .filter(Boolean);

    const imagePath =
      product.image ||
      product.mainImage ||
      product.main_image ||
      product.previewImage ||
      product.preview_image ||
      "";

    const coverPath = product.coverImage || product.cover_image || "";
    const thumbnailPath = product.thumbnailImage || product.thumbnail_image || "";

    const baseFolder =
      getFolderPath(imagePath) ||
      getFolderPath(coverPath) ||
      getFolderPath(thumbnailPath) ||
      product.publicBasePath ||
      product.public_base_path ||
      getProductFolderName(product);

    const standardMedia = [];

    if (imagePath) {
      standardMedia.push({
        mediaType: "preview",
        filePath: imagePath,
        fileName: getFileName(imagePath),
        imageBase: getImageBaseFromPath(imagePath),
        folderName: getFolderPath(imagePath) || baseFolder,
        altText: fallbackAlt,
        sortOrder: 1,
        isPrimary: true,
      });
    }

    if (coverPath && coverPath !== imagePath) {
      standardMedia.push({
        mediaType: "cover",
        filePath: coverPath,
        fileName: getFileName(coverPath),
        imageBase: getImageBaseFromPath(coverPath),
        folderName: getFolderPath(coverPath) || baseFolder,
        altText: `${product.title} cover image`,
        sortOrder: 2,
        isPrimary: false,
      });
    }

    if (!imagePath && thumbnailPath) {
      standardMedia.push({
        mediaType: "preview",
        filePath: thumbnailPath,
        fileName: getFileName(thumbnailPath),
        imageBase: getImageBaseFromPath(thumbnailPath),
        folderName: getFolderPath(thumbnailPath) || baseFolder,
        altText: fallbackAlt,
        sortOrder: 1,
        isPrimary: true,
      });
    }

    // Modern products already return their exact media list from the API.
    // Only probe legacy conventional filenames when no explicit media exists;
    // otherwise every missing guess triggers AVIF -> WebP -> JPG retries.
    if (baseFolder && directMedia.length === 0) {
      if (product.slug === "solitary-boat-golden-river-sunset-photography") {
        standardMedia.push({
          mediaType: "video",
          filePath: `${baseFolder}/golden-river-boat-wall-art-preview.mp4`,
          fileName: "golden-river-boat-wall-art-preview.mp4",
          imageBase: "golden-river-boat-wall-art-preview-video",
          folderName: baseFolder,
          altText: `${product.title} video preview`,
          sortOrder: 2,
          isPrimary: false,
        });
      }

      const standardFiles = [
        ["main.jpg", "preview", "Main Preview", 10],
        ["main.webp", "preview", "Main Preview", 10],
        ["main.avif", "preview", "Main Preview", 10],
        ["gallery-01.jpg", "gallery", "Gallery Image", 11],
        ["gallery-01.webp", "gallery", "Gallery Image", 11],
        ["gallery-01.avif", "gallery", "Gallery Image", 11],
        ["gallery-02.jpg", "gallery", "Gallery Image", 12],
        ["gallery-02.webp", "gallery", "Gallery Image", 12],
        ["gallery-02.avif", "gallery", "Gallery Image", 12],
        ["gallery-03.jpg", "gallery", "Gallery Image", 13],
        ["gallery-03.webp", "gallery", "Gallery Image", 13],
        ["gallery-03.avif", "gallery", "Gallery Image", 13],
        ["mockup-01.jpg", "mockup", "Room Mockup", 20],
        ["mockup-01.webp", "mockup", "Room Mockup", 20],
        ["mockup-01.png", "mockup", "Room Mockup", 20],
        ["mockup-02.jpg", "mockup", "Room Mockup", 21],
        ["mockup-02.webp", "mockup", "Room Mockup", 21],
        ["mockup-02.png", "mockup", "Room Mockup", 21],
        ["mockup-01-bedroom-wall-art.jpg", "mockup", "Bedroom Mockup", 22],
        ["mockup-01-bedroom-wall-art.png", "mockup", "Bedroom Mockup", 22],
        ["mockup-02-bedroom-wall-art.jpg", "mockup", "Bedroom Mockup", 23],
        ["mockup-02-bedroom-wall-art.png", "mockup", "Bedroom Mockup", 23],
        ["mockup-01-living-room-console.jpg", "mockup", "Living Room Mockup", 24],
        ["mockup-01-living-room-console.png", "mockup", "Living Room Mockup", 24],
        ["mockup-02-living-room-console.jpg", "mockup", "Living Room Mockup", 25],
        ["mockup-02-living-room-console.png", "mockup", "Living Room Mockup", 25],
        ["closeup-preview.jpg", "closeup", "Closeup Preview", 30],
        ["closeup-preview.png", "closeup", "Closeup Preview", 30],
        ["license-preview.jpg", "license", "License Preview", 31],
        ["license-preview.png", "license", "License Preview", 31],
        ["whats-included.jpg", "info", "What is Included", 32],
        ["whats-included.png", "info", "What is Included", 32],
      ];

      standardFiles.forEach(([fileName, type, label, sortOrder]) => {
        standardMedia.push({
          mediaType: type,
          filePath: `${baseFolder}/${fileName}`,
          fileName,
          imageBase: getImageBaseFromPath(fileName),
          folderName: baseFolder,
          altText: `${product.title} - ${label}`,
          sortOrder,
          isPrimary: false,
        });
      });
    }

    const uniqueMap = new Map();

    [...directMedia, ...standardMedia].forEach((item) => {
      if (!item.filePath) return;

      const uniqueKey = `${item.folderName || getFolderPath(item.filePath)}-${
        item.imageBase || getImageBaseFromPath(item.filePath)
      }`;

      if (!uniqueMap.has(uniqueKey)) {
        uniqueMap.set(uniqueKey, item);
      }
    });

    return Array.from(uniqueMap.values()).sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return Number(a.sortOrder || 999) - Number(b.sortOrder || 999);
    });
  }, [product]);

  const visibleGallery = useMemo(() => {
    return productGallery.filter((item) => !brokenImages[item.filePath]);
  }, [productGallery, brokenImages]);

  const selectedImage = visibleGallery[selectedImageIndex] || visibleGallery[0];

  useEffect(() => {
    if (selectedImageIndex > visibleGallery.length - 1) {
      setSelectedImageIndex(0);
    }
  }, [selectedImageIndex, visibleGallery.length]);

  const licenseOptions = product?.licenseOptions?.length
    ? product.licenseOptions
    : [{ label: "Digital Download", price: product?.price || 0 }];

  const license = licenseOptions[selectedLicense] || licenseOptions[0];

  const receiveItems = cleanReceiveItems(product?.includes);

  const related = useMemo(() => {
    if (!product) return [];

    const productKey = getProductKey(product);
    const currentBrandSlug = getProductBrandSlug(product);

    return allProducts
      .filter((item) => {
        const itemKey = getProductKey(item);
        const itemBrandSlug = getProductBrandSlug(item);

        return (
          itemKey !== productKey &&
          itemBrandSlug === currentBrandSlug &&
          item.status === "published"
        );
      })
      .slice(0, 4);
  }, [allProducts, product]);

  const handleImageError = (filePath) => {
    setBrokenImages((current) => ({
      ...current,
      [filePath]: true,
    }));
  };

  const goToPreviousImage = () => {
    if (!visibleGallery.length) return;

    setSelectedImageIndex((current) =>
      current === 0 ? visibleGallery.length - 1 : current - 1
    );
  };

  const goToNextImage = () => {
    if (!visibleGallery.length) return;

    setSelectedImageIndex((current) =>
      current === visibleGallery.length - 1 ? 0 : current + 1
    );
  };

  const handleBuyNow = async () => {
    setCheckoutError("");

    if (!product) {
      setCheckoutError("Product is not ready yet.");
      return;
    }

    try {
      setCheckoutLoading(true);

      const response = await fetch(buildApiUrl("/payments/create-checkout-session"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              productId: product.productId,
              licenseLabel: license.label,
              quantity: 1
            }
          ],
          customerEmail: customerEmail.trim() || undefined,
          couponCode: couponCode.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.url) {
        throw new Error(data.message || "Could not start Stripe Checkout.");
      }

      window.location.href = data.url;
    } catch (err) {
      setCheckoutError(err.message || "Checkout failed. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, license);
    navigate("/cart");
  };

  const renderOptimizedImage = ({
    mediaItem,
    variant,
    className,
    imgClassName,
    alt,
    loading,
    fetchPriority,
    objectFit,
    objectPosition,
    sizes,
    onError,
  }) => {
    const optimizedAvailable = hasOptimizedSource(product, mediaItem);

    if (optimizedAvailable) {
      return (
        <ResponsiveImage
          product={product}
          folderName={mediaItem.folderName || getProductFolderName(product)}
          imageBase={mediaItem.imageBase || getProductMainImageBase(product)}
          variant={variant}
          alt={alt}
          className={className}
          imgClassName={imgClassName}
          fallbackSrc={resolveAssetUrl(mediaItem.filePath)}
          objectFit={objectFit}
          objectPosition={objectPosition}
          loading={loading}
          fetchPriority={fetchPriority}
          sizes={sizes}
          onError={onError}
        />
      );
    }

    return (
      <img
        src={resolveAssetUrl(mediaItem.filePath)}
        alt={alt}
        className={imgClassName || className}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        onError={onError}
        draggable="false"
      />
    );
  };

  if (loading) {
    return (
      <main className="product-details-page not-found">
        <h1>Loading product...</h1>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="product-details-page not-found">
        <h1>{error || "Product not found"}</h1>
        <Link to="/shop" className="btn btn-gold">
          Back to Shop
        </Link>
      </main>
    );
  }

  return (
    <main className="product-details-page">
      <section className="product-details-layout">
        <div className="product-gallery-panel">
          <div className="product-gallery-shell">
            {visibleGallery.length > 1 && (
              <div className="product-thumb-list" aria-label="Product image gallery">
                {visibleGallery.map((item, index) => (
                  <button
                    key={`${item.filePath}-${index}`}
                    type="button"
                    className={`product-thumb-button ${
                      selectedImageIndex === index ? "active" : ""
                    }`}
                    style={isVideoMedia(item) ? { position: "relative" } : undefined}
                    onClick={() => setSelectedImageIndex(index)}
                    title={item.fileName}
                  >
                    {isVideoMedia(item) ? (
                      <>
                        <video
                          className="product-thumb-video"
                          src={resolveVideoUrl(item.filePath)}
                          muted
                          playsInline
                          preload="metadata"
                          style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
                          onError={() => handleImageError(item.filePath)}
                        />
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute", left: "50%", top: "50%",
                            width: 28, height: 28, display: "grid", placeItems: "center",
                            border: "1px solid rgba(255,255,255,.75)", borderRadius: "50%",
                            background: "rgba(5,9,11,.78)", color: "white",
                            transform: "translate(-50%,-50%)", fontSize: ".68rem"
                          }}
                        >▶</span>
                      </>
                    ) : renderOptimizedImage({
                        mediaItem: item,
                        variant: "thumb",
                        className: "product-thumb-image-wrap",
                        imgClassName: "product-thumb-image",
                        alt: item.altText || product.title,
                        loading: "lazy",
                        objectFit: "cover",
                        objectPosition: "center",
                        sizes: "84px",
                        onError: () => handleImageError(item.filePath),
                      })}
                  </button>
                ))}
              </div>
            )}

            <div className="product-main-image-wrap">
              {selectedImage ? (
                <>
                  {visibleGallery.length > 1 && (
                    <button
                      type="button"
                      className="product-gallery-nav product-gallery-nav-prev"
                      onClick={goToPreviousImage}
                      aria-label="Previous product image"
                    >
                      ‹
                    </button>
                  )}

                  {isVideoMedia(selectedImage) ? (
                    <video
                      key={selectedImage.filePath}
                      className="product-main-video"
                      src={resolveVideoUrl(selectedImage.filePath)}
                      poster={resolveAssetUrl(product.image || product.mainImage || "")}
                      controls
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", background: "#05090b" }}
                      onError={() => handleImageError(selectedImage.filePath)}
                    >
                      Your browser does not support MP4 video.
                    </video>
                  ) : renderOptimizedImage({
                      mediaItem: selectedImage,
                      variant: "full",
                      className: "product-main-image-container",
                      imgClassName: "product-main-image",
                      alt: selectedImage.altText || product.altText || product.title,
                      loading: "eager",
                      fetchPriority: "high",
                      objectFit: "contain",
                      objectPosition: "center",
                      sizes: "(max-width: 900px) 100vw, 58vw",
                      onError: () => handleImageError(selectedImage.filePath),
                    })}

                  {visibleGallery.length > 1 && (
                    <button
                      type="button"
                      className="product-gallery-nav product-gallery-nav-next"
                      onClick={goToNextImage}
                      aria-label="Next product image"
                    >
                      ›
                    </button>
                  )}
                </>
              ) : (
                <div className="product-image-placeholder">
                  Product image will appear here.
                </div>
              )}

              {selectedImage && (
                <div className="product-gallery-caption">
                  <span>{selectedImage.mediaType}</span>
                  <strong>
                    {selectedImageIndex + 1} / {visibleGallery.length}
                  </strong>
                </div>
              )}
            </div>
          </div>

          <div className="gallery-included-card">
            <h3>What you will receive</h3>

            <div className="included-badges gallery-included-badges">
              {receiveItems.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </div>

        <aside className="product-info-panel">
          <p className="product-brand-label">{product.brand}</p>

          <h1>{product.title}</h1>

          <p className="product-short">{product.shortDescription}</p>

          <div className="license-box">
            <h3>Choose your license</h3>

            {licenseOptions.map((option, index) => (
              <button
                key={`${option.label}-${index}`}
                className={selectedLicense === index ? "active" : ""}
                onClick={() => setSelectedLicense(index)}
                type="button"
              >
                <span>{option.label}</span>
                <strong>${Number(option.price || 0).toFixed(2)}</strong>
              </button>
            ))}
          </div>

          <div className="checkout-box compact-checkout-box">
            <div className="single-buy-row">
              <button
                className="buy-button buy-button-with-price"
                type="button"
                onClick={handleBuyNow}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  "Opening Checkout..."
                ) : (
                  <>
                    <span>Buy Now</span>
                    <strong>— ${Number(license.price || 0).toFixed(2)}</strong>
                  </>
                )}
              </button>
              <button
                className="buy-button buy-button-with-price buy-button-ghost"
                type="button"
                onClick={handleAddToCart}
                disabled={checkoutLoading}
              >
                <span>Add to Cart</span>
              </button>
            </div>

            {checkoutError && <div className="checkout-error">{checkoutError}</div>}

            <details className="optional-email-box">
              <summary>Email or coupon code</summary>

              <input
                type="email"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
                placeholder="you@example.com"
              />
              <input
                type="text"
                value={couponCode}
                onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                placeholder="Coupon code (optional)"
              />
            </details>

            <p className="checkout-note">
              Secure payment is handled by Stripe. Your email is collected at
              checkout for the receipt and download recovery.
            </p>

            <div className="purchase-trust-badges">
              <span>Instant download</span>
              <span>Secure Stripe checkout</span>
              <span>Up to 5 downloads</span>
            </div>
          </div>
        </aside>
      </section>

      <section className="product-story-section">
        <div>
          <p className="eyebrow">Behind the Lens</p>

          <h2>
            {product.storyTitle ||
              product.story_title ||
              "A Visual Story from MayaChitro"}
          </h2>

          <p>
            {product.story || "Story content will be added soon from admin panel."}
          </p>
        </div>

        <div>
          <p className="eyebrow">Image Use & Details</p>

          <h2>
            {product.usageTitle ||
              product.usage_title ||
              product.detailsTitle ||
              product.details_title ||
              "Perfect For Wall Art, Decor & Creative Projects"}
          </h2>

          <p>{product.description || product.shortDescription}</p>
        </div>
      </section>

      <section className="related-products-section">
        <div className="details-section-heading">
          <h2>Related Products</h2>

          <Link to={product.brandSlug ? `/shop?brand=${product.brandSlug}` : "/shop"}>
            View more →
          </Link>
        </div>

        <div className="related-grid">
          {related.map((item) => (
            <ProductCard key={item.id || item.productId} product={item} compact />
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetails;
